/**
 * segmentPaletteFromFill.js — PURE logic for the palette-derived Segment Color
 * build (Gordon 2026-07-16, flag REACT_APP_SEGMENT_PALETTE_COLOR).
 *
 * Two responsibilities, both dependency-light (no React / no redux / no I/O):
 *
 *  (A) resolveSegmentPaletteSeed(settings) — decide HOW to seed the Segment
 *      Palette from the segment's FILL:
 *        · Image / GIF    -> { kind: "image", url }   (feed the SAME url through
 *                             the Palette Generator's generateColorsFromImage —
 *                             we do NOT re-implement extraction)
 *        · Gradient       -> { kind: "colors", colors } (its stops)
 *        · Pattern        -> { kind: "colors", colors } (its fg/bg, or css stops)
 *        · solid Color / nothing -> { kind: "color", color } (the existing
 *                             color path — generateScheme(color))
 *      This is what lets the EXISTING Segment Palette be generated from the fill
 *      instead of only from the solid color.
 *
 *  (B) resolveSegmentColor(settings, { enabled }) — the effective Segment Color
 *      for the wheel slice + open-segment frame border + left accent:
 *        · flag OFF                    -> settings.color (byte-identical)
 *        · manual override (see below) -> settings.color (preserved)
 *        · else                        -> the Segment Palette PRIMARY
 *                                         (settings.paletteData.colors[0].hex)
 *      For a color-derived palette the primary === settings.color (identity, no
 *      visible change); only a FILL-derived palette makes the primary differ, so
 *      the border harmonizes with the fill. A manual pick sets
 *      settings.segmentColorManual (see useSegmentSetting) which pins the color.
 *
 * resolveSegmentFill (fillModelFeed) already normalises image/gif/pattern from
 * the V2 blob / snake fields / legacy fields; gradient stays in the V2 blob as
 * `fill.gradient` (resolveSegmentFill skips it), so we read that directly.
 */

import { resolveSegmentFill } from "./fillModelFeed";
// W1-1: the ONE shared casing-agnostic normaliser. `normalizeSegmentSettings`
// wraps caseConvert's shallow snake->camel (dependency-free — it stays out of the
// react-froala barrel that breaks Jest; see src/utils/caseConvert.js's header)
// with null-safety, and is the SAME normalisation ViewWheel/index.jsx:637 applies.
// Reading through it here means no palette/color consumer spells a raw
// snake/camel key — the casing bug class is impossible by construction.
import { normalizeSegmentSettings } from "../../../../../utils/segmentSettingsAccessor";

/** A #rgb / #rrggbb / #rrggbbaa hex (with the #). */
const HEX_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export const isHexColor = (c) => typeof c === "string" && HEX_RE.test(c.trim());

/**
 * Extract the color stops from a gradient MODEL ({ stops: [{color}] }) or from a
 * raw gradient/pattern CSS string (any #hex or rgb()/rgba() literals, in order).
 * Pure; returns a de-duplicated, order-preserving array of color strings.
 *
 * @param {object|string} gradientOrCss
 * @returns {string[]}
 */
export const extractGradientStops = (gradientOrCss) => {
  if (!gradientOrCss) return [];

  let raw = [];
  if (
    typeof gradientOrCss === "object" &&
    Array.isArray(gradientOrCss.stops)
  ) {
    raw = gradientOrCss.stops
      .map((s) => (s && typeof s === "object" ? s.color : s))
      .filter(Boolean);
  } else if (typeof gradientOrCss === "string") {
    raw =
      gradientOrCss.match(
        /#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})\b|rgba?\([^)]*\)/g
      ) || [];
  }

  // De-dup (case-insensitive for hex) while preserving first-seen order.
  const seen = new Set();
  const out = [];
  for (const c of raw) {
    const key = String(c).trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(String(c).trim());
  }
  return out;
};

/**
 * Decide how to seed the Segment Palette from the segment's fill.
 *
 * @param {object} segmentSettings a segment `settings` object
 * @returns {{kind:"image",url:string} | {kind:"colors",colors:string[]} | {kind:"color",color:string|undefined}}
 */
export const resolveSegmentPaletteSeed = (segmentSettings) => {
  // Casing-agnostic by construction: read a canonical camelCase view (idempotent
  // on the camelCase every real caller already passes — see W1-1).
  const s = normalizeSegmentSettings(segmentSettings);

  // Gradient lives in the V2 blob as type "gradient" (resolveSegmentFill skips
  // it because it carries no wedge media/pattern source). Read its stops here.
  const blob = s.fill;
  if (blob && blob.type === "gradient") {
    const stops = extractGradientStops(blob.gradient);
    if (stops.length) return { kind: "colors", colors: stops };
  }

  const model = resolveSegmentFill(s);
  if (model) {
    if (model.type === "image") {
      const url = model.image && model.image.url;
      if (url) return { kind: "image", url };
    }
    if (model.type === "gif") {
      const url = model.gif && model.gif.url;
      if (url) return { kind: "image", url };
    }
    if (model.type === "pattern") {
      const p = model.pattern || {};
      // A pattern's palette = its foreground/background colors (segmentFill.js
      // pattern model), or any color literals in a css pattern string.
      const fromCss = extractGradientStops(p.css);
      const fromFgBg = [p.fg, p.bg].filter(isHexColor);
      const patternCol = isHexColor(s.patternColor) ? [s.patternColor] : [];
      const colors = (fromFgBg.length ? fromFgBg : fromCss).concat(patternCol);
      const deduped = extractGradientStops(colors.join(","));
      if (deduped.length) return { kind: "colors", colors: deduped };
    }
  }

  // Legacy gradient css persisted in segmentBgColor when currentBg === 3.
  if (Number(s.currentBg) === 3 && typeof s.segmentBgColor === "string") {
    const stops = extractGradientStops(s.segmentBgColor);
    if (stops.length > 1) return { kind: "colors", colors: stops };
  }

  return { kind: "color", color: s.color };
};

/**
 * Build a Segment-Palette object (the shape usePaletteColor / savePalette /
 * updateSegmentColorPalette expect) from an ordered list of hex colors — used by
 * the gradient/pattern (no-API) path. colors[0] is the LOCKED primary (the
 * Segment Color is derived from it).
 *
 * @param {string[]} hexes
 * @param {object} [opts]
 * @param {string} [opts.paletteName]
 * @param {(x?:any)=>(string|number)} [opts.makeId] id factory (getRandomId)
 * @returns {object} palette object
 */
export const buildFillPalette = (hexes, opts = {}) => {
  const {
    paletteName = "Fill palette",
    makeId = () => Math.random().toString(36).slice(2),
  } = opts;
  const list = (Array.isArray(hexes) ? hexes : []).filter(Boolean);
  return {
    index: 0,
    paletteName,
    source: "fill",
    colors: list.map((hex, ind) => ({
      hex,
      id: makeId(),
      selected: false,
      locked: ind === 0, // first color is the segment-color primary
      index: ind,
    })),
    id: makeId(),
    selected: true,
  };
};

/** The palette PRIMARY (colors[0].hex), or undefined when there is no palette. */
export const paletteePrimary = (paletteData) => {
  const colors = paletteData && paletteData.colors;
  if (!Array.isArray(colors) || colors.length === 0) return undefined;
  const first = colors[0];
  return first && first.hex ? first.hex : undefined;
};

/**
 * The effective Segment Color: palette-derived by default, manual override
 * preserved. See file header (B).
 *
 * @param {object} segmentSettings
 * @param {{enabled?:boolean}} [opts]
 * @returns {string|undefined}
 */
export const resolveSegmentColor = (segmentSettings, opts = {}) => {
  // Casing-agnostic by construction: canonical camelCase view (idempotent on the
  // camelCase every real caller passes — ViewWheel:637 / withPaletteResolvedColors
  // convert first). W1-1: no raw snake/camel key read here anymore.
  const s = normalizeSegmentSettings(segmentSettings);
  const stored = s.color;
  if (!opts.enabled) return stored;
  // An explicit manual pick pins the color regardless of the fill palette.
  if (s.segmentColorManual === true) return stored;
  const primary = paletteePrimary(s.paletteData);
  return primary || stored;
};

/**
 * Card/tile-surface convenience wrapper (Gallery, Explore, Segment Library,
 * Folder listings, table rows — ISP-9999 gallery-pill-gif). Given a wheel's
 * segment list (as fetched, `wheel_segments`-shaped or a legacy flat shape),
 * returns a NEW array where each segment's `color` is EXACTLY what
 * pages/ViewWheel/index.jsx (`getWheelData`, line ~620-640) would render for
 * the same segment — mirrored, not reinvented:
 *
 *   1. CASING (qa-validator FAIL 2026-07-20, evidence: a real GET
 *      /api/v2/wheel/2641/ returns segment `settings` in SNAKE_CASE —
 *      `palette_data`, `segment_color_manual`, never `paletteData` —
 *      `resolveSegmentColor` reads camelCase only, so the raw API shape made
 *      the palette-primary law silently INERT on every real card). ViewWheel
 *      never hits this because its OWN fetch path already runs
 *      `convertSnakeToCamel(s.settings)` (ViewWheel:637) before anything else
 *      touches segment settings. This wrapper now does the exact same shallow
 *      conversion via the shared `normalizeSegmentSettings` accessor
 *      (src/utils/segmentSettingsAccessor) at the same point in the pipeline —
 *      and since W1-1, resolveSegmentColor itself normalises too, so the read is
 *      casing-safe regardless of how it is reached.
 *   2. COLOR AUTHORITY (qa-validator, evidence: a real wheel's top-level
 *      `segment.color` and `segment.settings.color` DIVERGE on every
 *      segment). ViewWheel's merge is `{...segment, ...segment.settings}`
 *      (settings wins) UNCONDITIONALLY — independent of the
 *      SEGMENT_PALETTE_COLOR_ENABLED flag, which only additionally applies a
 *      palette-primary override on top. This wrapper replicates BOTH steps in
 *      the same order, so a card's resolved color can never diverge from
 *      what View/Edit would show for the identical segment.
 *
 * Reads `seg.settings` when present (the V2 shape ViewWheel reads); a
 * missing/empty `settings` (older/flatter shapes, e.g. Gallery's V1
 * `activity_wheel` items) makes the merge a no-op and this falls back to the
 * segment's own already-stored top-level `color` — so a shape ViewWheel's law
 * doesn't apply to can never get WORSE, only a shape it does apply to gets
 * fixed. Pure; does not mutate input.
 *
 * @param {Array<object>} segments
 * @param {{enabled?: boolean}} [opts] enabled gates ONLY the palette-primary
 *   override (mirrors SEGMENT_PALETTE_COLOR_ENABLED in ViewWheel) — the
 *   settings-color merge itself is unconditional, same as ViewWheel.
 * @returns {Array<object>}
 */
export const withPaletteResolvedColors = (segments, opts = {}) => {
  const list = Array.isArray(segments) ? segments : [];
  return list.map((seg) => {
    const camelSettings = normalizeSegmentSettings(seg && seg.settings);
    const merged = { ...seg, ...camelSettings };
    const resolved = resolveSegmentColor(camelSettings, {
      enabled: !!opts.enabled,
    });
    // resolved is undefined only when camelSettings carried no color at all
    // (e.g. the V1/no-.settings shapes) — keep whatever the merge already
    // produced (the segment's own top-level color) rather than blanking it.
    merged.color = resolved || merged.color;
    return merged;
  });
};
